// Canonical navigation registry — the single source of truth for the workspace
// item lists that SideNav renders AND the personal-menu editor enumerates
// (PRIME-NAV-UX-001, locked decision 4). This module was extracted verbatim
// from SideNav.jsx so both the rail and the editor derive "available pages"
// from the exact same catalog; the extraction is behavior-preserving and is
// proven by the existing SideNav.test.jsx / SideNav.siteAccess.test.jsx suites.
//
// Nothing here renders JSX — the item objects only hold references to icon
// component functions, so this stays a plain data module importable by pure
// logic (navPreferences.js) and by the editor alike.

import {
  IconDashboard,
  IconDelivery,
  IconRoadmap,
  IconFinance,
  IconLifecycle,
  IconAI,
  IconConnector,
  IconLineage,
  IconShield,
  IconHome,
  IconBell,
  IconExport,
  IconTarget,
  IconBriefcase,
  IconKanban,
  IconBranch,
  IconChat,
  IconKey,
  IconClipboard,
  IconBookmark,
  IconBinoculars,
  IconUsers,
  IconDoc,
  IconSparkles,
  IconGate,
} from '../components/icons.jsx';
import { rbac } from '@dha-nexus/shared';

const P = rbac.ROUTE_PERMISSIONS;

// Dynamically-injected rail items (session-state gated). Kept here alongside the
// base catalog so the route→metadata lookup can resolve icons/labels for a saved
// custom layout that happens to include one of them.
export const SITE_ACCESS_ITEM = {
  to: '/admin/site-access',
  label: 'CAC Approval',
  icon: IconShield,
  allow: P['/admin/site-access'],
};

export const PM_GUIDE_RESULTS_ITEM = {
  to: '/admin/guide-results',
  label: 'PM guide results',
  icon: IconClipboard,
  allow: P['/admin/guide-results'],
  alwaysVisible: true,
};

// Seven-workspace information architecture. Extracted unchanged from SideNav.jsx
// (see that file's original comment for the AC / policy rationale enforced by
// SideNav.test.jsx: canonical URLs only, ≤ 8 items per workspace, allow lists
// mirroring rbac.ROUTE_PERMISSIONS).
export const NAV_WORKSPACES = [
  {
    label: 'Home',
    items: [
      { to: '/home', label: 'Mission Brief', icon: IconHome, allow: P['/home'] },
      { to: '/notifications', label: 'Inbox', icon: IconBell, allow: P['/notifications'] },
      { to: '/feedback', label: 'My Feedback', icon: IconChat, allow: P['/feedback'] },
      { to: '/saved-views', label: 'Saved views', icon: IconBookmark, allow: P['/saved-views'] },
      { to: '/access/status', label: 'My access', icon: IconKey, allow: P['/access/status'] },
      { to: '/me', label: 'Profile', icon: IconUsers, allow: P['/me'] },
      { to: '/settings', label: 'Settings', icon: IconSparkles, allow: P['/settings'] },
      { to: '/help', label: 'Help & getting started', icon: IconDoc, allow: P['/help'] },
    ],
  },
  {
    label: 'Intake',
    items: [
      {
        to: '/project-factory',
        label: 'Intake queue',
        icon: IconClipboard,
        allow: P['/project-factory'],
      },
      {
        to: '/project-factory/requests/new',
        label: 'New request',
        icon: IconDoc,
        allow: P['/project-factory'],
      },
      { to: '/rapids', label: 'RAPID', icon: IconTarget, allow: P['/rapids'] },
    ],
  },
  {
    label: 'Command',
    items: [
      { to: '/executive', label: 'Command Center', icon: IconDashboard, allow: P['/executive'] },
      { to: '/loe-dashboard', label: 'LOE Board', icon: IconTarget, allow: P['/loe-dashboard'] },
      {
        to: '/portfolios',
        label: 'Portfolios & Programs',
        icon: IconBriefcase,
        allow: P['/portfolios'],
      },
      { to: '/lifecycle', label: 'Lifecycle', icon: IconLifecycle, allow: P['/lifecycle'] },
      { to: '/roadmap', label: 'Roadmap', icon: IconRoadmap, allow: P['/roadmap'] },
      { to: '/financials', label: 'Financials', icon: IconFinance, allow: P['/financials'] },
      {
        to: '/decision-support',
        label: 'Decision support',
        icon: IconBranch,
        allow: P['/decision-support'],
      },
      {
        to: '/market-research',
        label: 'Market research',
        icon: IconBinoculars,
        allow: P['/market-research'],
      },
    ],
  },
  {
    label: 'Delivery',
    items: [
      { to: '/projects', label: 'Projects', icon: IconKanban, allow: P['/projects'] },
      { to: '/delivery', label: 'Board', icon: IconDelivery, allow: P['/delivery'] },
      { to: '/actions', label: 'Action Center', icon: IconChat, allow: P['/actions'] },
      { to: '/meetings', label: 'Meetings', icon: IconClipboard, allow: P['/meetings'] },
      { to: '/resources', label: 'Resources', icon: IconUsers, allow: P['/resources'] },
      { to: '/timesheets', label: 'Time', icon: IconClipboard, allow: P['/timesheets'] },
      { to: '/governance', label: 'Governance', icon: IconShield, allow: P['/governance'] },
    ],
  },
  {
    label: 'Knowledge & Products',
    items: [
      { to: '/knowledge', label: 'Library', icon: IconBookmark, allow: P['/knowledge'] },
      {
        to: '/knowledge/lessons-learned',
        label: 'Lessons learned',
        icon: IconDoc,
        allow: P['/knowledge'],
      },
      { to: '/products', label: 'Product factory', icon: IconExport, allow: P['/products'] },
      {
        to: '/status-packs',
        label: 'Status packs',
        icon: IconClipboard,
        allow: P['/status-packs'],
      },
      { to: '/ai/ask', label: 'Ask PRIME', icon: IconChat, allow: P['/ai/ask'] },
    ],
  },
  {
    label: 'Data & Trust',
    items: [
      { to: '/admin/data', label: 'Connectors', icon: IconConnector, allow: P['/admin/data'] },
      {
        to: '/admin/ingestion',
        label: 'Ingestion',
        icon: IconGate,
        allow: P['/admin/ingestion'],
      },
      { to: '/admin/lineage', label: 'Lineage', icon: IconLineage, allow: P['/admin/lineage'] },
      {
        to: '/admin/custom-fields',
        label: 'Configuration',
        icon: IconKey,
        allow: P['/admin/custom-fields'],
      },
      {
        to: '/ai/orchestration',
        label: 'AI Operations',
        icon: IconAI,
        allow: P['/ai/orchestration'],
      },
      {
        to: '/feedback/queue',
        label: 'Feedback queue',
        icon: IconChat,
        allow: P['/feedback/queue'],
      },
      { to: '/uat', label: 'UAT readiness', icon: IconClipboard, allow: P['/uat'] },
    ],
  },
  {
    label: 'Administration',
    items: [
      { to: '/admin/rbac', label: 'RBAC Admin', icon: IconShield, allow: P['/admin/rbac'] },
      { to: '/admin/users', label: 'Users', icon: IconUsers, allow: P['/admin/users'] },
      {
        to: '/admin/authentication',
        label: 'Authentication',
        icon: IconKey,
        allow: P['/admin/authentication'],
      },
      {
        to: '/admin/access-requests',
        label: 'Access',
        icon: IconKey,
        allow: P['/admin/access-requests'],
      },
      { to: '/admin/audit', label: 'Audit', icon: IconDoc, allow: P['/admin/audit'] },
      {
        to: '/admin/activity',
        label: 'Activity Reports',
        icon: IconDoc,
        allow: P['/admin/activity'],
      },
      {
        to: '/admin/compliance',
        label: 'Compliance',
        icon: IconGate,
        allow: P['/admin/compliance'],
      },
    ],
  },
];

// The workspace label is the stable "canonical section key" persisted as
// layout[].source (locked decision 1). Exported so the editor / preferences
// logic never has to hardcode it a second time.
export const HOME_SECTION_KEY = 'Home';
export const HOME_ROUTE = '/home';

// Reproduce SideNav's dynamic per-session injection so the rail and any consumer
// resolve the identical workspace set. Behavior-preserving with the original
// SideNav.jsx `renderedWorkspaces` map.
export function resolveWorkspaces({ isRootAdmin = false, canSeeSiteAccess = false } = {}) {
  return NAV_WORKSPACES.map((workspace) => {
    if (workspace.label === 'Data & Trust' && isRootAdmin) {
      return { ...workspace, items: workspace.items.filter((item) => item.to !== '/uat') };
    }
    if (workspace.label !== 'Administration') return workspace;

    const items = [...workspace.items];
    if (isRootAdmin) {
      const usersIndex = items.findIndex((item) => item.to === '/admin/users');
      items.splice(usersIndex + 1, 0, PM_GUIDE_RESULTS_ITEM);
    }
    if (canSeeSiteAccess) items.push(SITE_ACCESS_ITEM);
    return { ...workspace, items };
  });
}

// Flat route → item-metadata lookup (icon + label + allow + owning section key),
// including the dynamically-injected items so a saved custom layout that references
// them still resolves an icon/label at render time. Built once at module load.
export const NAV_ITEM_BY_ROUTE = (() => {
  const map = new Map();
  for (const workspace of NAV_WORKSPACES) {
    for (const item of workspace.items) {
      map.set(item.to, { ...item, section: workspace.label });
    }
  }
  for (const item of [SITE_ACCESS_ITEM, PM_GUIDE_RESULTS_ITEM]) {
    map.set(item.to, { ...item, section: 'Administration' });
  }
  return map;
})();

// Ordered list of canonical section keys (workspace labels) for stable section
// ordering in the editor's "available pages" grouping and recommended layout.
export const NAV_SECTION_ORDER = NAV_WORKSPACES.map((w) => w.label);

/** Resolve a route to its display metadata (icon/label/section/allow) or null. */
export function getNavItem(route) {
  return NAV_ITEM_BY_ROUTE.get(route) || null;
}
